/**
 * Gx gallery dots + light arrows + autoplay.
 *
 * Dawn's product media gallery is a scroll-snap <slider-component> (see
 * `SliderComponent` in global.js) with its own prev/next buttons + "1/3"
 * counter. We don't touch that file: gx-gallery-dots.css hides that
 * counter/arrows, and this reads/drives the SAME slider-component instance
 * instead of reimplementing any slide logic --
 *   - `gallery.currentPage` / `gallery.totalPages` for state
 *   - `gallery.prevButton` / `gallery.nextButton` -- Dawn's own (hidden)
 *     buttons, whose `.disabled` state and `.click()` behavior we reuse
 *     directly for our own light overlay arrows
 *   - `gallery.setSlidePosition(px)` to jump straight to a page (for dots,
 *     and to loop back to the first image on autoplay)
 *   - its public `slideChanged` CustomEvent to stay in sync
 *
 * Only runs at all when main-product.liquid's "Pagination de la galerie
 * (Gx)" setting is "dots" (that's what conditionally loads this file).
 */
const GX_GALLERY_AUTOPLAY_MS = 4000;

function initGxGalleryDots(gallery) {
  // Matches SliderComponent's own guard: no slider/next button means it
  // never computed totalPages, so there's nothing to paginate or autoplay.
  if (!gallery.slider || !gallery.nextButton) return;

  const dotsContainer = document.createElement('div');
  dotsContainer.className = 'gx-gallery-dots';
  gallery.insertAdjacentElement('afterend', dotsContainer);

  // Reuse Dawn's own prev/next buttons' icon markup (and their existing
  // rotation CSS, via the shared slider-button--prev/--next classes) for a
  // lighter, floating pair of arrows overlaid on the image instead.
  const prevArrow = document.createElement('button');
  prevArrow.type = 'button';
  prevArrow.className = 'gx-gallery-nav-arrow gx-gallery-nav-arrow--prev slider-button slider-button--prev';
  prevArrow.setAttribute('aria-label', "Image précédente");
  prevArrow.innerHTML = gallery.prevButton.innerHTML;
  prevArrow.addEventListener('click', () => {
    gallery.prevButton.click();
    restartAutoplay();
  });

  const nextArrow = document.createElement('button');
  nextArrow.type = 'button';
  nextArrow.className = 'gx-gallery-nav-arrow gx-gallery-nav-arrow--next slider-button slider-button--next';
  nextArrow.setAttribute('aria-label', 'Image suivante');
  nextArrow.innerHTML = gallery.nextButton.innerHTML;
  nextArrow.addEventListener('click', () => {
    gallery.nextButton.click();
    restartAutoplay();
  });

  // Anchored to the current slide <li> itself (not the whole
  // slider-component, which can be much taller than one image -- e.g. on
  // desktop, where the gallery_layout is "stacked" and all images render
  // in a single tall column rather than a one-at-a-time slider). Moving the
  // arrows into whichever <li> is current keeps them on that image instead
  // of floating over the middle of the whole stack.
  function anchorArrowsTo(slideElement) {
    if (!slideElement || slideElement.contains(prevArrow)) return;
    slideElement.style.position = 'relative';
    slideElement.append(prevArrow, nextArrow);
  }

  anchorArrowsTo(gallery.sliderItemsToShow && gallery.sliderItemsToShow[(gallery.currentPage || 1) - 1]);

  function buildDots(total) {
    dotsContainer.innerHTML = '';
    for (let i = 0; i < total; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'gx-gallery-dots__dot';
      dot.setAttribute('aria-label', `Aller à l'image ${i + 1}`);
      dot.addEventListener('click', () => {
        gallery.setSlidePosition(i * gallery.sliderItemOffset);
        restartAutoplay();
      });
      dotsContainer.appendChild(dot);
    }
  }

  function render(currentPage, currentElement) {
    const total = gallery.totalPages || 1;
    const page = currentPage || 1;
    if (dotsContainer.children.length !== total) buildDots(total);
    dotsContainer.hidden = total < 2;

    Array.from(dotsContainer.children).forEach((dot, i) => {
      dot.setAttribute('aria-current', i === page - 1 ? 'true' : 'false');
    });

    // Computed directly from total/page rather than Dawn's own
    // prevButton/nextButton.disabled: for a single-image gallery,
    // SliderComponent's initPages() returns before ever setting `disabled`
    // on those buttons at all, which left our arrows showing with nothing
    // to navigate to. This covers that case too, not just the two ends of
    // a multi-image gallery.
    prevArrow.hidden = total < 2 || page <= 1;
    nextArrow.hidden = total < 2 || page >= total;

    anchorArrowsTo(currentElement || (gallery.sliderItemsToShow && gallery.sliderItemsToShow[(currentPage || 1) - 1]));
  }

  // --- Autoplay: cycles forward automatically, looping back to the first
  // image at the end (Dawn's own buttons don't loop; we do it ourselves by
  // jumping to position 0 once "next" is disabled). ---
  let autoplayTimer = null;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function playAutoplay() {
    if (autoplayTimer || reducedMotion || (gallery.totalPages || 1) < 2) return;
    autoplayTimer = setInterval(() => {
      if (gallery.nextButton.disabled) gallery.setSlidePosition(0);
      else gallery.nextButton.click();
    }, GX_GALLERY_AUTOPLAY_MS);
  }

  function pauseAutoplay() {
    if (autoplayTimer) {
      clearInterval(autoplayTimer);
      autoplayTimer = null;
    }
  }

  function restartAutoplay() {
    pauseAutoplay();
    playAutoplay();
  }

  gallery.addEventListener('mouseenter', pauseAutoplay);
  gallery.addEventListener('mouseleave', playAutoplay);
  gallery.addEventListener('focusin', pauseAutoplay);
  gallery.addEventListener('focusout', playAutoplay);
  document.addEventListener('visibilitychange', () => (document.hidden ? pauseAutoplay() : playAutoplay()));

  // SliderComponent's constructor (in global.js, loaded before this script)
  // already ran and computed currentPage/totalPages by the time we get
  // here, so render the current state immediately...
  render(gallery.currentPage);
  // ...then stay in sync as the visitor scrolls/uses the arrows/dots, or
  // resizes across a breakpoint that changes totalPages.
  gallery.addEventListener('slideChanged', (event) => render(event.detail.currentPage, event.detail.currentElement));

  playAutoplay();
}

document.querySelectorAll('slider-component[id^="GalleryViewer-"]').forEach(initGxGalleryDots);
